import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createInstagramStory,
  getAllInstagramStories,
  getInstagramStoryByID,
  InstagramStoryImageUpload,
  updateInstagramStory,
} from "./services/instagramStory.services";
import { clearInstagramStoryDetails } from "./instagramStorySlice";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";
import FormFileUpload from "../../components/formFields/FormFileUpload";
import FormImageUpload from "../../components/formFields/FormImageUpload";

const CreateInstagramStory: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const InstagramStoryDetail = useAppSelector(
    (state) => state.instagramStory.InstagramStoryDetail
  );
  const InstagramStoryID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector(
    (state) => state.instagramStory.formLoading
  );

  useEffect(() => {
    if (InstagramStoryID) {
      dispatch(getInstagramStoryByID(InstagramStoryID));
    }
  }, [InstagramStoryID]);

  useEffect(() => {
    return () => {
      dispatch(clearInstagramStoryDetails());
      dispatch(closed());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const ThumbnailImage =
      values?.ThumbnailImage && typeof values?.ThumbnailImage !== "string"
        ? values.ThumbnailImage[0].getRawFile()
        : "";
    const VideoUpload =
      values?.VideoUpload && typeof values?.VideoUpload !== "string"
        ? values.VideoUpload[0].getRawFile()
        : "";

    try {
      if (!InstagramStoryID) {
        const insertPayload = {
          Title: values.Title,
          URL: values.URL,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createInstagramStory(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            ((values?.VideoUpload && typeof values?.VideoUpload !== "string") ||
              (values?.ThumbnailImage &&
                typeof values?.ThumbnailImage !== "string")) &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("ID", response?.payload?.Data?.ID);
            ImageData.append("VideoUpload", VideoUpload);
            ImageData.append("ThumbnailImage", ThumbnailImage);
            await dispatch(InstagramStoryImageUpload(ImageData));
          }
          dispatch(closed());
          dispatch(clearInstagramStoryDetails());
          dispatch(getAllInstagramStories());
        }
      } else {
        const editPayload = {
          ID: InstagramStoryID,
          Title: values.Title,
          URL: values.URL,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateInstagramStory(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.VideoUpload && typeof values?.VideoUpload !== "string") ||
            (values?.ThumbnailImage &&
              typeof values?.ThumbnailImage !== "string")
          ) {
            ImageData.append("ID", InstagramStoryID);
            ImageData.append("VideoUpload", VideoUpload);
            ImageData.append("ThumbnailImage", ThumbnailImage);
            await dispatch(InstagramStoryImageUpload(ImageData));
          }
          dispatch(closed());
          dispatch(clearInstagramStoryDetails());
          dispatch(getAllInstagramStories());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={InstagramStoryDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
            <GridLayoutItem
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "17px 0 8px",
              }}
            >
              <Typography.h4 className="m-0">
                {InstagramStoryID
                  ? "Update Instagram Story"
                  : "Add Instagram Story"}
              </Typography.h4>
              <IconButton
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
                icon={RxCross1}
              />
            </GridLayoutItem>

            <GridLayoutItem>
              <Field
                name="Title"
                label="Title"
                placeholder="Title"
                astrike={true}
                component={FormTextField}
                validator={requiredValidator}
                onChange={(e: any) => {
                  formRenderProps.onChange("URL", {
                    value: e?.value
                      ? e?.value?.toLowerCase()?.trim(" ")?.replace(/\s+/g, "-")
                      : "",
                  });
                }}
              />
            </GridLayoutItem>
            <GridLayoutItem>
              <Field
                name="URL"
                label="URL"
                placeholder="URL"
                astrike={true}
                component={FormTextField}
                validator={requiredValidator}
                onInput={(e: any) => {
                  e.target.value = e?.target?.value
                    ?.toLowerCase()
                    ?.trim(" ")
                    ?.replace(/\s+/g, "-");
                }}
              />
            </GridLayoutItem>
            {InstagramStoryID && InstagramStoryDetail?.VideoUpload && (
              <GridLayoutItem className="mt-3">
                <video
                  src={formRenderProps.valueGetter("VideoUpload")}
                  poster="images/poster.jpg"
                  controls
                  preload="metadata"
                  style={{ height: "150px", width: "100%" }}
                >
                  Your browser does not support the <code>video</code> element.
                </video>
              </GridLayoutItem>
            )}
            <GridLayoutItem>
              <Field
                name="VideoUpload"
                type="file"
                accept="video/*"
                label="Video"
                component={FormFileUpload}
              />
            </GridLayoutItem>
            <GridLayoutItem>
              <Field
                name="ThumbnailImage"
                type="file"
                accept="image/*"
                label="Thumbnail Image"
                component={FormImageUpload}
              />
            </GridLayoutItem>
            <GridLayoutItem>
              <Field
                id={"IsActive"}
                name={"IsActive"}
                label={"Status"}
                component={FormSelectionField}
                validator={requiredValidator}
                astrike={true}
                placeholder="i.e. Active"
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status.value,
                    label: status.label,
                  };
                })}
              />
            </GridLayoutItem>

            <GridLayoutItem
              style={{
                textAlign: "end",
                marginTop: "20px",
              }}
            >
              <ButtonWithLoading
                label={InstagramStoryID ? "Update" : "Save"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />

              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 10 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateInstagramStory;
